/**
 * Who owns the bottom-right corner.
 *
 * Two things want to float there: the Undead assistant bubble, which is on EVERY page, and the
 * slot settings Save button, which appears the moment you edit a field. Left alone they would
 * sit on top of each other - and the one that loses is decided by paint order, which is not a
 * decision anyone made.
 *
 * So the corner is a stack with a registry. Anything pinned there reserves its height; anything
 * that must sit above the pile reads the total and lifts by it. The assistant bubble is the
 * floater (it yields), the Save button is the anchor (it is contextual and urgent, so it keeps
 * the corner). Nothing here knows about either one specifically - a third floater would work the
 * same way.
 *
 * Kept as a module-level store rather than context because the two participants are in different
 * subtrees (Layout renders the bubble, the settings page renders Save) and a context spanning
 * both would have to live above the router.
 */

const heights = new Map();     // id -> reserved height in px
const listeners = new Set();

function emit() {
  const total = reservedHeight();
  listeners.forEach((fn) => {
    try { fn(total); } catch (e) { /* a bad listener must not break the others */ }
  });
}

/**
 * Claim the corner. Re-reserving the same id updates its height rather than stacking twice,
 * so a component may call this on every render without leaking.
 */
export function reserveFab(id, height) {
  if (!id) return;
  const h = Number(height);
  const next = Number.isFinite(h) && h > 0 ? h : 0;
  if (heights.get(id) === next) return;    // no-op: do not wake listeners for nothing
  heights.set(id, next);
  emit();
}

/** Give the corner back. Safe to call for an id that never reserved. */
export function releaseFab(id) {
  if (heights.delete(id)) emit();
}

/** Total px reserved at the corner right now. */
export function reservedHeight() {
  let total = 0;
  heights.forEach((h) => { total += h; });
  return total;
}

/** Subscribe to changes; returns the unsubscribe. Fires with the total on every change. */
export function subscribeFabStack(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Tests only - the store is module state and would otherwise leak between cases. */
export function __resetFabStack() {
  heights.clear();
  listeners.clear();
}
